/**
 * Dismisses any delivered push notifications when the user actually opens
 * the app — so the badge / notification center clears as soon as they engage.
 *
 * Handles 3 surfaces:
 *   1. Capacitor iOS / Android  → OneSignal Cordova plugin
 *      `removeAllDeliveredNotifications()` clears everything from the
 *      iOS Notification Center / Android tray.
 *   2. Web Push (browser/PWA via service worker) → ServiceWorker
 *      registration → `getNotifications()` → close each.
 *   3. OneSignal Web SDK         → `window.OneSignal.Notifications.clearAll()`
 *      best-effort; only available when the v16 SDK is loaded.
 *
 * Idempotent: safe to call multiple times. Never throws — failures are
 * silently swallowed since the goal (clear the tray) is a "nice to have".
 */
export async function clearDeliveredPushNotifications() {
  // 1) Capacitor native — OneSignal Cordova plugin clears the iOS Notification
  //    Center / Android tray for the current app.
  try {
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      const OS = window.plugins?.OneSignal || window.cordova?.plugins?.OneSignal;
      OS?.Notifications?.clearAll?.();
    }
  } catch (_) { /* native plugin not available, fall through */ }

  // 2) Web Push — close every notification still pinned by our SW
  try {
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        const notifs = await reg.getNotifications();
        notifs.forEach((n) => { try { n.close(); } catch (_) {} });
      }
    }
  } catch (_) { /* SW unavailable */ }

  // 3) OneSignal Web SDK v16 — clear from the OS notification center
  try {
    if (typeof window !== "undefined" && window.OneSignal?.Notifications?.clearAll) {
      window.OneSignal.Notifications.clearAll();
    }
  } catch (_) { /* SDK not loaded yet */ }
}

/**
 * Hook helper: attaches a listener that calls clearDeliveredPushNotifications()
 *   - On mount (first open)
 *   - Each time the tab becomes visible again (user returns from another app)
 *   - On focus (desktop browser)
 *
 * Returns a cleanup function for React `useEffect`.
 */
export function attachAutoClearOnAppOpen() {
  // Initial run — clears whatever was already sitting in the tray.
  clearDeliveredPushNotifications();

  const onVisible = () => {
    if (document.visibilityState === "visible") {
      clearDeliveredPushNotifications();
    }
  };
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", clearDeliveredPushNotifications);
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", clearDeliveredPushNotifications);
  };
}
